import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {CommonModule, Location} from '@angular/common';
import {ActivatedRoute, Router} from "@angular/router";
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule
} from "devextreme-angular";
import {
    AssetDto,
    AssetsClient,
    AssetType,
    ChecklistDto,
    ChecklistClient,
    CreateChecklistCommand,
    MachineAttachmentCheckDto,
    RepairChecklistCommand,
    PermissionType,
    AccessType,
    AuthService,
    ChecklistCondition
} from "@services";
import EnumUtils from "../../../../shared/enum-utils";
import notify from "devextreme/ui/notify";
import {firstValueFrom} from "rxjs";
import {MachineAttachmentCheckComponent} from "./components/machine-attachment-check/machine-attachment-check.component";
import {ChecklistQuestionsComponent} from "./components/checklist-questions/checklist-questions.component";
import {UserAvatarEmailComponent} from "../../../../components/utils/user-avatar-email/user-avatar-email.component";
import {CapitalCasePipe} from "../../../../pipes/capital-case.pipe";
import {AssetSelectComponent} from "./components/asset-select/asset-select.component";
import {AssetTypePipe} from "../../../../pipes/asset-type.pipe";
import {NotesComponent, NoteType} from "../../../../components/library/notes/notes.component";

@Component({
    selector: 'app-checklist-details',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        MachineAttachmentCheckComponent,
        ChecklistQuestionsComponent,
        UserAvatarEmailComponent,
        UserAvatarEmailComponent,
        CapitalCasePipe,
        AssetSelectComponent,
        NotesComponent
    ],
    providers: [AssetTypePipe],
    templateUrl: './checklist-details.component.html',
    styleUrl: './checklist-details.component.scss',
})
export class ChecklistDetailsComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private location = inject(Location);
    private router = inject(Router);
    private checklistsClient = inject(ChecklistClient);
    private assetsClient = inject(AssetsClient);
    private assetTypePipe = inject(AssetTypePipe);
    protected auth = inject(AuthService);

    @ViewChild('validationGroup', {static: true}) validationGroup: DxValidationGroupComponent;
    @ViewChild(ChecklistQuestionsComponent) questionsComponent!: ChecklistQuestionsComponent;
    @ViewChild(NotesComponent) notesComponent: NotesComponent;

    protected checklistId: string | null = null;
    protected checklist: ChecklistDto = new ChecklistDto();
    protected isLoading = false;
    protected isSubmitting = false;

    protected assetTypeOptions = EnumUtils.getEnumOptions(AssetType, (v) => this.assetTypePipe.transform(v)).filter(x => [
        AssetType.MhBtS,
        AssetType.MhCounterBalance,
        AssetType.MhCounterBalanceLayPick,
        AssetType.MhReach,
        AssetType.MhWalkies,
        AssetType.HandheldScanner,
        AssetType.Scrubbers,
        AssetType.ShuntTruck,
        AssetType.ScissorLift,
        AssetType.WrapMachine,
        AssetType.TwoWayRadio
    ].includes(x.value as AssetType));
    protected conditionOptions = EnumUtils.getEnumOptions(ChecklistCondition);

    protected selectedMachine: AssetDto | null = null;
    protected readonly AssetType = AssetType;
    protected readonly NoteType = NoteType;

    get isReadOnly() {
        return !!this.checklistId;
    }

    get isTwoWayRadioRequired(): boolean{
        return !this.isMhType(this.checklist.assetType);
    }

    get hasDefects(): boolean {
        return this.questionsComponent?.hasDefects ?? false;
    }

    ngOnInit(): void {
        this.checklistId = this.route.snapshot.paramMap.get('id');
        if (this.checklistId) {
            this.loadChecklist();
        } else {
            this.initializeNewChecklist();
        }
    }

    private initializeNewChecklist() {
        this.checklist = new ChecklistDto();
        this.checklist.answers = [];
        this.initAttachmentChecks();
    }

    private initAttachmentChecks() {
        this.checklist.mobileLabelPrinterCheck = new MachineAttachmentCheckDto();
        this.checklist.tabletComputerCheck = new MachineAttachmentCheckDto();
        this.checklist.rfScanGunCheck = new MachineAttachmentCheckDto();
        this.checklist.clampLayerAttachCheck = new MachineAttachmentCheckDto();
        this.checklist.mhBatteryCheck = new MachineAttachmentCheckDto();
        this.checklist.slipSheetAttachCheck = new MachineAttachmentCheckDto();
    }

    private loadChecklist() {
        this.isLoading = true;
        this.checklistsClient.getChecklist(this.checklistId!).subscribe({
            next: async (data) => {
                this.checklist = data;

                if (!this.checklist.mobileLabelPrinterCheck) this.checklist.mobileLabelPrinterCheck = new MachineAttachmentCheckDto();
                if (!this.checklist.tabletComputerCheck) this.checklist.tabletComputerCheck = new MachineAttachmentCheckDto();
                if (!this.checklist.rfScanGunCheck) this.checklist.rfScanGunCheck = new MachineAttachmentCheckDto();
                if (!this.checklist.clampLayerAttachCheck) this.checklist.clampLayerAttachCheck = new MachineAttachmentCheckDto();
                if (!this.checklist.mhBatteryCheck) this.checklist.mhBatteryCheck = new MachineAttachmentCheckDto();
                if (!this.checklist.slipSheetAttachCheck) this.checklist.slipSheetAttachCheck = new MachineAttachmentCheckDto();

                if (this.checklist.machineId) {
                    await this.loadMachineDetails(this.checklist.machineId);
                }

                this.isLoading = false;
            },
            error: () => {
                notify('Failed to load checklist', 'error', 3000);
                this.isLoading = false;
                this.location.back();
            }
        });
    }

    async onAssetTypeChanged(e: any) {
        if (!e.value) return;

        this.checklist.machineId = undefined;
        this.checklist.twoWayRadioId = undefined;
        this.checklist.equipmentId = undefined;
        this.selectedMachine = null;

        if (this.questionsComponent) this.questionsComponent.clear();
    }

    async onMachineChanged(e: any) {
        if (!e.value) {
            this.selectedMachine = null;
            if (this.questionsComponent) this.questionsComponent.clear();
            return;
        }

        try {
            await this.loadMachineDetails(e.value);
        } catch (err) {
            console.error(err);
        }
    }

    private async loadMachineDetails(id: string) {
        try {
            this.selectedMachine = await firstValueFrom(this.assetsClient.getAsset(id));
        } catch (e) {
            console.error("Failed to load machine details", e);
        }
    }

    get showMachineSelector(): boolean {
        return this.isMhType(this.checklist.assetType);
    }

    get showTwoWayRadioSelector(): boolean {
        return this.checklist.assetType === AssetType.TwoWayRadio || this.isMhType(this.checklist.assetType);
    }

    get showRadioShuttleCardSelector(): boolean {
        return this.checklist.assetType === AssetType.MhReach;
    }

    get showEquipmentSelector(): boolean {
        return !this.showMachineSelector && !this.showTwoWayRadioSelector && !!this.checklist.assetType;
    }

    get showMobileLabelPrinterCheck(): boolean {
        if (!this.isMhType(this.checklist.assetType))
            return false;

        return !!this.selectedMachine?.attachments?.mobileLabelPrinterId;
    }

    get showTabletCheck(): boolean {
        if (!this.isMhType(this.checklist.assetType)) return false;
        return !!this.selectedMachine?.attachments?.tabletComputerId;
    }

    get showRfGunCheck(): boolean {
        if (!this.isMhType(this.checklist.assetType)) return false;
        return !!this.selectedMachine?.attachments?.rfScanGunId;
    }

    get showClampLayerCheck(): boolean {
        if (!this.isMhType(this.checklist.assetType)) return false;
        return !!this.selectedMachine?.attachments?.clampLayerAttachId;
    }

    get showSlipSheetCheck(): boolean {
        if (!this.isMhType(this.checklist.assetType)) return false;
        return !!this.selectedMachine?.attachments?.slipSheetAttachId;
    }

    get showBattery(): boolean {
        if (!this.isMhType(this.checklist.assetType)) return false;
        return !!this.selectedMachine?.attachments?.mheBatteryId;
    }

    get selectedAssetId(): string | undefined | null {
        return this.checklist.machineId || this.checklist.equipmentId || this.checklist.twoWayRadioId;
    }

    save() {
        const result = this.validationGroup.instance.validate();
        if (!result.isValid) {
            notify({message: 'Please fix validation errors', position: 'bottom center'}, 'error', 3000);
            return;
        }

        if (this.notesComponent && !this.notesComponent.validate()) {
            notify({message: 'Please add at least one note', position: 'bottom center'}, 'error', 3000);
            return;
        }

        this.isSubmitting = true;
        this.checklist.answers = this.questionsComponent.getAnswers();

        const cmd = new CreateChecklistCommand({...this.checklist});
        this.checklistsClient.createChecklist(cmd).subscribe({
            next: async (id) => {
                if (this.notesComponent) {
                    await this.notesComponent.saveQueuedNotes(id);
                }
                notify('Checklist created successfully', 'success', 3000);
                this.isSubmitting = false;
                await this.router.navigate(['/checklist/edit', id], {replaceUrl: true});
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message || 'Failed to create checklist';
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            }
        });
    }

    repair() {
        if (!this.checklistId) return;

        this.isSubmitting = true;
        const cmd = new RepairChecklistCommand({id: this.checklistId});

        this.checklistsClient.repairChecklist(cmd).subscribe({
            next: () => {
                notify('Checklist repaired successfully', 'success', 3000);
                this.loadChecklist();
                this.isSubmitting = false;
            },
            error: (errors: Array<any>) => {
                const msg = errors?.[0]?.message || 'Failed to repair checklist';
                notify(msg, 'error', 3000);
                this.isSubmitting = false;
            }
        });
    }

    cancel() {
        this.location.back();
    }

    isMhType(type?: AssetType): boolean {
        if (type == null) return false;
        return [
            AssetType.MhBtS,
            AssetType.MhCounterBalance,
            AssetType.MhCounterBalanceLayPick,
            AssetType.MhReach,
            AssetType.MhWalkies
        ].includes(type);
    }

    protected readonly CheckListCondition = ChecklistCondition;
    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;
}
